import { AppListItem } from "../../../Items/AppListItem";
import { ReactElement, useMemo } from "react";
import { AppColorViewModel } from "client/Api/Apps/common";
import {
	convertAppColor,
	convertAppImage,
	convertMaintenanceWindow,
	convertTag,
	tryConvertAppToNavigationParameters
} from "client/Features/Search/RecentSearch/Persistence/convertPersistedData";
import { AppImageDisplayInfo } from "client/Api/AppIcon/common";
import {
	GlobalSearchResultItemKind,
	MaintenanceWindowAppModel
} from "client/Api/TitleBar/GlobalSearch/globalSearch";
import { TagViewModel } from "client/Api/Tags/tags";
import { AppNavigationParameters, useAppNavigationHandler } from "../../../Navigation/useAppNavigationHandler";
import { generateTrackingId } from "client/Misc/utils";
import { GlobalSearchItem } from "../../../GlobalSearchItem/GlobalSearchItem";
import { TimeZone } from "client/DateAndTime/timezones";
import { ValidGlobalSearchItemPersistedModel } from "client/Features/Search/RecentSearch/Persistence/validation";

export type AppItemViewProps = {
	item: ValidGlobalSearchItemPersistedModel<GlobalSearchResultItemKind.App>;
	itemIndex: number;
	onRemove: () => void;
	timezone: TimeZone;
	tabIndex: number;
};

export const AppItemView = (props: AppItemViewProps): ReactElement => {
	const {
		item,
		itemIndex,
		onRemove,
		timezone,
		tabIndex
	} = props;

	const {
		id,
		category,
		color: persistedColor,
		image: persistedImage,
		maintenanceWindow: persistedMaintenanceWindow,
		name,
		tags: persistedTags
	} = item.app;

	const color = useMemo(
		(): AppColorViewModel => {
			return convertAppColor(persistedColor);
		},
		[persistedColor]
	);

	const image = useMemo(
		(): AppImageDisplayInfo => {
			return convertAppImage(persistedImage);
		},
		[persistedImage]
	);

	const maintenanceWindow = useMemo(
		(): MaintenanceWindowAppModel | null => {
			if (!persistedMaintenanceWindow) {
				return null;
			}

			return convertMaintenanceWindow(persistedMaintenanceWindow);
		},
		[persistedMaintenanceWindow]
	);

	const tags = useMemo(
		(): readonly TagViewModel[] => {
			return persistedTags.map(t => convertTag(t));
		},
		[persistedTags]
	);

	const appConversion = useMemo(
		(): TryResult<AppNavigationParameters> => {
			return tryConvertAppToNavigationParameters(item.app);
		},
		[item.app]
	);

	const openHandler: () => void = useAppNavigationHandler({
		id,
		parameters: appConversion.wasSuccessful
			? appConversion.result
			: "unresolved",
		maintenanceWindow,
		timezone,
		recentlyOpenedItem: item
	});

	return (
		<GlobalSearchItem
			trackingId={generateTrackingId("app-item", `|${id}`)}
			onClick={openHandler}
		>
			<AppListItem
				id={id}
				category={category}
				color={color}
				image={image}
				maintenanceWindow={maintenanceWindow}
				name={name}
				tags={tags}
				onRemove={onRemove}
				itemIndex={itemIndex}
				tabIndex={tabIndex}
				onExecute={openHandler}
			/>
		</GlobalSearchItem>
	);
};
AppItemView.displayName = "AppItemView";
